/**
 * Sentinel `queryFn` for cache-attached observers (`<Items>`,
 * `useListIds`, `<Field>` hooks).
 *
 * These observers run with `enabled: false` and share a `queryKey`
 * with a real "parent" hook (`useGetMarkets`, `useGetMarket`, etc.).
 * Their `queryFn` is supposed to never run — but several TanStack
 * code paths invoke it anyway:
 *
 *   - `queryClient.refetchQueries(...)` ignores `enabled` and calls
 *     `Query.fetch()` using `Query.options.queryFn`.
 *   - `Query.options.queryFn` is whichever observer last called
 *     `setOptions` — typically a subscriber, because subscribers
 *     mount AFTER the parent (leaves render after the page hook).
 *   - The SDK itself fires `qc.refetchQueries({queryKey})` from
 *     several places (NATS reacquire, connectivity reconnect, etc.).
 *
 * **Resolution model — return cache, or delegate the cold fetch**. The factory
 * captures `queryClient + queryKey` so it can read the cache when invoked.
 * Returning existing data lets TanStack run its success
 * path:
 *
 *   1. `setData(currentData)` — structural sharing collapses to the
 *      same reference, no observer re-renders for `data`.
 *   2. `dispatch({type: 'success'})` → `fetchStatus: 'idle'`.
 *   3. The parent observer's parallel real refetch (if one was
 *      issued) hits the network normally — `obs.refetch()` uses the
 *      parent's options, not the Query's.
 *
 * **Why not `CancelledError({silent, revert})`?** TanStack's catch
 * block in `Query.fetch` short-circuits with
 * `return this.#retryer.promise` on `silent: true` — it never
 * dispatches a `success`/`error` action, so `fetchStatus` stays
 * stuck at `'fetching'` forever. `revert: true` also doesn't reset
 * `fetchStatus`. The only paths back to `fetchStatus: 'idle'` from a
 * rejection are `dispatch({type: 'error'})` (sets `status: 'error'`
 * visibly) and `dispatch({type: 'success'})` from a resolved
 * promise. So we resolve.
 *
 * **Identification**. Subscriber `queryFn`s are tagged with a Symbol
 * marker so `useSdkScreenRefresh`'s observer-iteration logic can
 * skip them and target the parent. The marker is module-local — only
 * `isSubscriberQueryFn(fn)` exposes the check, and `Symbol.for`
 * guarantees correctness across module-instance duplication (which
 * can otherwise hit you with double-installed SDK copies).
 */

import type { QueryClient, QueryFunction, QueryKey, QueryMeta } from '@tanstack/react-query';
import { getCachedQuery } from './get-cached-query.js';

const SUBSCRIBER_MARKER = Symbol.for('@morfi/backend-sdk-v2/subscriberQueryFn');

/**
 * Subscriber observers share the owning query's key, so TanStack applies
 * their options to that same Query. Carry the existing metadata forward or
 * the subscriber would erase fields such as `specName`, `viewerSensitive`,
 * and `persist` that cache invalidation and auth isolation depend on.
 */
export function getSubscriberQueryMetaOptions(
  queryClient: QueryClient,
  queryKey: QueryKey,
): { readonly meta?: QueryMeta } {
  const meta = getCachedQuery(queryClient, { queryKey })?.meta;
  return meta === undefined ? {} : { meta };
}

/**
 * Build a subscriber `queryFn` bound to a specific cache entry. It resolves
 * with cached data when present. On a cold cache it waits one microtask, then
 * delegates to a mounted data-owning observer; the placeholder is only the
 * last resort when no such observer exists.
 *
 * The returned function is type-cast to `QueryFunction<TData>` so it
 * matches `useQuery`'s required `queryFn` signature; the actual runtime
 * value is whatever's in the cache, which is `TData | undefined` —
 * downstream `select`s already handle the undefined case.
 */
export function createSubscriberQueryFn<TData>(
  queryClient: QueryClient,
  queryKey: QueryKey,
): QueryFunction<TData> {
  const fn: QueryFunction<TData> = (context) => {
    const current = queryClient.getQueryData<TData>(queryKey);
    if (current !== undefined) return Promise.resolve(current);

    // A cold query can schedule its first fetch after every observer has
    // mounted. Subscriber observers mount after the data-owning parent, so
    // TanStack's shared Query may have latched this sentinel as its queryFn
    // before that first fetch starts. Returning the empty placeholder here
    // would mark the cold query successful with zero rows and suppress the
    // real HTTP request forever (staleTime is Infinity for realtime lists).
    //
    // Delegate only the cache-empty fetch to the mounted data-owning
    // observer. Once data exists, the cached-data branch above preserves the
    // sentinel contract for imperative refetches.
    // Observer subscriptions are installed during React's passive-effect
    // flush. A subscriber can be the observer that starts the fetch while the
    // parent observer is scheduled later in that same flush, so defer the
    // lookup by one microtask before deciding no parent exists.
    return Promise.resolve().then(() => {
      const landed = queryClient.getQueryData<TData>(queryKey);
      if (landed !== undefined) return landed;

      const query = getCachedQuery(queryClient, { queryKey });
      if (query !== undefined) {
        for (const observer of query.observers) {
          const parentQueryFn = observer.options.queryFn;
          if (typeof parentQueryFn !== 'function' || isSubscriberQueryFn(parentQueryFn)) continue;
          return (parentQueryFn as QueryFunction<TData>)(context);
        }
      }

      return EMPTY_PLACEHOLDER as TData;
    });
  };
  (fn as unknown as Record<symbol, unknown>)[SUBSCRIBER_MARKER] = true;
  return fn;
}

/**
 * Check whether a `queryFn` was produced by `createSubscriberQueryFn`
 * (or the deprecated singleton). Used by `useSdkScreenRefresh` to skip
 * subscriber observers when iterating a Query's observers to find the
 * data-owning one to refetch.
 */
export function isSubscriberQueryFn(fn: unknown): boolean {
  if (typeof fn !== 'function') return false;
  return (fn as unknown as Record<symbol, unknown>)[SUBSCRIBER_MARKER] === true;
}

/**
 * Placeholder returned when a subscriber `queryFn` runs before the
 * parent has populated the cache. Frozen so accidental writes throw
 * instead of corrupting downstream selectors. Subscriber `select`s
 * read `.data` or row-id fields — both yield `undefined` here, which
 * matches every selector's existing "no rows yet" fallback.
 */
const EMPTY_PLACEHOLDER: Readonly<Record<string, unknown>> = Object.freeze({});

/**
 * Back-compat singleton — retained so existing imports don't break.
 * Rejects loudly if invoked (every call site should now use the
 * factory). Marked with the Symbol so the predicate still identifies it.
 *
 * @deprecated Use `createSubscriberQueryFn(queryClient, queryKey)`.
 */
export const subscriberQueryFn: QueryFunction<unknown> = (() => {
  const fn: QueryFunction<unknown> = () =>
    Promise.reject(
      new Error(
        '[morfi-sdk] legacy subscriberQueryFn singleton invoked. ' +
          'Every subscriber call site should use `createSubscriberQueryFn`.',
      ),
    );
  (fn as unknown as Record<symbol, unknown>)[SUBSCRIBER_MARKER] = true;
  return fn;
})();
